import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  inject,
  Optional,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { appConfigService } from '@app/config';
import { HierarchyListViewProviderDirective } from '@app/providers';
import { ParentItemProviderService } from '@app/shared';
import { DynamicFormService } from '@ntap/common';
import {
  AttributeStateService,
  AuthRequestOptions,
  ItemView,
  ItemViewProviderDirective,
  NtapGlobalConstants,
  Util
} from '@ntap/core';
import { PrimeNGInputDropdownComponent } from '@ntap/primeng';
import { first, map, Observable, Subject, tap } from 'rxjs';
import { EarhivUiComponents } from '..';

export type RokCuvanjaResult = {
  godinaIstekaCuvanja: number;
  postupakPoIsteku: number;
  trajanjeRokaCuvanja: number;
  vrijemePocetkaCuvanja: Date;
  klasaId: number;
  naziv: string;
  opis: string;
  pocetakRokaCuvanja: string;
};

export class ChangedTemplate {
  klasaSet: boolean;
  klasaId: number;

  constructor(klasaSet: boolean, klasaId: number) {
    this.klasaSet = klasaSet;
    this.klasaId = klasaId;
  }
}

@Component({
  selector: 'app-rok-cuvanja',
  templateUrl: './rok-cuvanja.component.html',
  styleUrls: ['./rok-cuvanjal.component.scss']
})
export class RokCuvanjaComponent
  extends PrimeNGInputDropdownComponent
  implements AfterViewInit
{
  @ViewChild(HierarchyListViewProviderDirective)
  listViewProvider: HierarchyListViewProviderDirective;
  @ViewChildren(ItemViewProviderDirective<ItemView>) itemViewProviders: any;

  private httpClient = inject(HttpClient);
  private config = inject(appConfigService);
  private itemViewProvider: ItemViewProviderDirective<ItemView> = inject(
    ItemViewProviderDirective<ItemView>
  );
  private klasaSetSubject = new Subject();
  INIT_COMP_ID = EarhivUiComponents.TREELISTVIEW;
  currentListViewName = 'poseban_popis_gradiva_s_rokovima_cuvanja_select';
  currentListViewState = {};
  currentListIdentifier = 'sif~poseban_popis_gradiva_s_rokovima_cuvanja~id';
  readonly ROK_CUVANJA_TYPE = 'sv~rok_cuvanja';
  formControlNames = [
    Util.getFullAttrName(this.ROK_CUVANJA_TYPE, 'trajanje_roka_cuvanja'),
    Util.getFullAttrName(this.ROK_CUVANJA_TYPE, 'vrijeme_pocetka_roka_cuvanja'),
    Util.getFullAttrName(
      this.ROK_CUVANJA_TYPE,
      'postupak_po_isteku_roka_cuvanja'
    ),
    Util.getFullAttrName(this.ROK_CUVANJA_TYPE, 'istek_roka_cuvanja')
  ];

  dialogVisible = false;
  isAcceptButtonDisabled$: Observable<boolean>;
  constructor(
    formService: DynamicFormService,
    attributeStateService: AttributeStateService,
    @Optional() protected parentItemProvider: ParentItemProviderService
  ) {
    super(formService, attributeStateService);
    console.log('Custom primeNg rokCuvanja component constructor!');
  }

  ngAfterViewInit(): void {
    this.isAcceptButtonDisabled$ = this.listViewProvider.selectedItems$.pipe(
      map((items) => !items || items.length < 1)
    );
  }

  openDialog(): void {
    this.dialogVisible = true;
  }

  closeDialog(): void {
    this.dialogVisible = false;
  }

  clearInputField(): void {
    this.formControl.setValue(null);
    this.changeViewMetadata(true, false, -1);
  }

  acceptNode(): void {
    this.listViewProvider.selectedItems$
      .pipe(
        first(),
        tap((items: any) => {
          if (items.length > 0) {
            const id = items[0][this.currentListIdentifier];
            this.formControl.setValue(id);
            if (id) {
              this.changeViewMetadata(false, true, id);
            }
            this.closeDialog();
          }
        })
      )
      .subscribe();
  }

  fetchAndSetDataFromKlasaId(klasaId: any) {
    const parentItem$ =
      this.parentItemProvider.parentItem$ ?? this.itemViewProvider.item$;
    parentItem$.pipe(first()).subscribe((parentItem: any) => {
      const parentId =
        parentItem[
          Util.getFullAttrName(
            parentItem[NtapGlobalConstants.ATTR_$mainType],
            NtapGlobalConstants.ATTR_id
          )
        ];
      const url = `${this.config.apiHost}rok-cuvanja?klasaId=${klasaId}&jedinicaGradivaId=${parentId}`;
      return this.httpClient
        .get<RokCuvanjaResult>(url, new AuthRequestOptions())
        .pipe(
          tap((response) => {
            this.setFormGroupValues(response);
            this.klasaSetSubject.next(new ChangedTemplate(false, -1));
          })
        )
        .subscribe();
    });
  }

  setFormGroupValues(rokCuvanja: RokCuvanjaResult): void {
    DynamicFormService.setControlValue(
      this,
      this.formService.formGroup.controls[
        Util.getFullAttrName(this.ROK_CUVANJA_TYPE, 'trajanje_roka_cuvanja')
      ],
      rokCuvanja.trajanjeRokaCuvanja
    );
    DynamicFormService.setControlValue(
      this,
      this.formService.formGroup.controls[
        Util.getFullAttrName(
          this.ROK_CUVANJA_TYPE,
          'postupak_po_isteku_roka_cuvanja'
        )
      ],
      rokCuvanja.postupakPoIsteku
    );
    DynamicFormService.setControlValue(
      this,
      this.formService.formGroup.controls[
        Util.getFullAttrName(this.ROK_CUVANJA_TYPE, 'istek_roka_cuvanja')
      ],
      rokCuvanja.godinaIstekaCuvanja
    );
    DynamicFormService.setControlValue(
      this,
      this.formService.formGroup.controls[
        Util.getFullAttrName(this.ROK_CUVANJA_TYPE, 'klasa_id')
      ],
      rokCuvanja.klasaId
    );
    DynamicFormService.setControlValue(
      this,
      this.formService.formGroup.controls[
        Util.getFullAttrName(this.ROK_CUVANJA_TYPE, 'naziv')
      ],
      rokCuvanja.naziv
    );
    DynamicFormService.setControlValue(
      this,
      this.formService.formGroup.controls[
        Util.getFullAttrName(this.ROK_CUVANJA_TYPE, 'opis')
      ],
      rokCuvanja.opis
    );
    DynamicFormService.setControlValue(
      this,
      this.formService.formGroup.controls[
        Util.getFullAttrName(this.ROK_CUVANJA_TYPE, 'pocetak_roka_cuvanja')
      ],
      rokCuvanja.pocetakRokaCuvanja
    );
  }

  changeViewMetadata(clearKlasa: boolean, klasaSet: boolean, klasaId: number) {
    const viewname = this.itemViewProvider.viewName;
    const viewstate: any = this.itemViewProvider.viewState;
    viewstate['$clearKlasa'] = clearKlasa;
    viewstate['$klasaSet'] = klasaSet;
    viewstate['$klasaId'] = klasaId;
    this.itemViewProvider.viewName = '';
    this.itemViewProvider.viewState = {};
    this.itemViewProvider.viewName = viewname;
    this.itemViewProvider.viewState = viewstate;
  }

  customSelectableFn(nodeData: any): boolean {
    return (
      !!nodeData['sif~poseban_popis_gradiva_s_rokovima_cuvanja~rok_cuvanja'] &&
      !!nodeData[
        'sif~poseban_popis_gradiva_s_rokovima_cuvanja~pocetak_roka_cuvanja'
      ]
    );
  }
}
